import { supabase } from '../supabaseClient';

export const DRONE_MAPPING_BUCKET = 'mapeamentos-drone';
export const DRONE_MAPPING_TABLE = 'mapeamentos_drone';
// Mesma lista aplicada no banco pela função climorax_drone_mapping_paid().
export const DRONE_MAPPING_PAID_PLAN_IDS = Object.freeze(['essential', 'professional', 'management', 'enterprise']);
export const MAX_DRONE_MAPPING_FILE_SIZE = 250 * 1024 * 1024;

const TIFF_EXTENSIONS = new Set(['tif', 'tiff']);

export class DroneMappingError extends Error {
  constructor(message, code = 'DRONE_MAPPING_INVALID') {
    super(message);
    this.name = 'DroneMappingError';
    this.code = code;
  }
}

export function droneMappingAvailable(user) {
  if (!user) return false;
  if (user.is_admin) return true;
  return DRONE_MAPPING_PAID_PLAN_IDS.includes(user.plano);
}

export function formatDroneMappingSize(size) {
  const bytes = Number(size || 0);
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function validateDroneMappingFile(file) {
  if (!file) throw new DroneMappingError('Selecione o arquivo do mapeamento.');
  if (file.size <= 0) throw new DroneMappingError('O arquivo está vazio.');
  if (file.size > MAX_DRONE_MAPPING_FILE_SIZE) {
    throw new DroneMappingError('O limite por mapeamento é 250 MB. Gere o COG com mais compressão ou menor resolução.', 'DRONE_MAPPING_TOO_LARGE');
  }
  const extension = String(file.name || '').split('.').pop()?.toLowerCase() || '';
  if (!TIFF_EXTENSIONS.has(extension)) {
    throw new DroneMappingError('Envie o ortomosaico em GeoTIFF otimizado para nuvem (COG, extensão .tif).', 'DRONE_MAPPING_TYPE');
  }
  return true;
}

export async function listDroneMappings(propriedadeId) {
  try {
    const { data, error } = await supabase
      .from(DRONE_MAPPING_TABLE)
      .select('id, propriedade_id, user_id, nome, data_voo, arquivo_path, tamanho_bytes, observacoes, criado_em')
      .eq('propriedade_id', propriedadeId)
      .order('data_voo', { ascending: false })
      .order('criado_em', { ascending: false });
    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (err) {
    console.error('Erro ao listar mapeamentos de drone:', err);
    return { success: false, error: err.message, data: [] };
  }
}

export async function publishDroneMapping({ propriedadeId, userId, file, nome, dataVoo }) {
  try {
    validateDroneMappingFile(file);
    const id = globalThis.crypto?.randomUUID
      ? globalThis.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const arquivoPath = `${propriedadeId}/${id}.tif`;

    const { error: uploadError } = await supabase.storage
      .from(DRONE_MAPPING_BUCKET)
      .upload(arquivoPath, file, {
        cacheControl: '31536000',
        contentType: 'image/tiff',
        upsert: false,
      });
    if (uploadError) throw uploadError;

    const registro = {
      propriedade_id: propriedadeId,
      user_id: userId,
      nome: String(nome || file.name).trim().slice(0, 120) || 'Mapeamento de drone',
      data_voo: dataVoo || new Date().toISOString().slice(0, 10),
      arquivo_path: arquivoPath,
      tamanho_bytes: file.size,
    };

    const { data, error } = await supabase
      .from(DRONE_MAPPING_TABLE)
      .insert([registro])
      .select()
      .single();

    if (error) {
      // Não deixar arquivo órfão quando o registro falha.
      await supabase.storage.from(DRONE_MAPPING_BUCKET).remove([arquivoPath]);
      throw error;
    }

    return { success: true, data };
  } catch (err) {
    console.error('Erro ao publicar mapeamento de drone:', err);
    return { success: false, error: err.message };
  }
}

export async function deleteDroneMapping(mapeamento) {
  try {
    const { error } = await supabase
      .from(DRONE_MAPPING_TABLE)
      .delete()
      .eq('id', mapeamento.id);
    if (error) throw error;

    if (mapeamento.arquivo_path) {
      const { error: storageError } = await supabase.storage
        .from(DRONE_MAPPING_BUCKET)
        .remove([mapeamento.arquivo_path]);
      if (storageError) console.warn('Registro excluído, mas o arquivo permaneceu no bucket:', storageError.message);
    }

    return { success: true };
  } catch (err) {
    console.error('Erro ao excluir mapeamento de drone:', err);
    return { success: false, error: err.message };
  }
}

export async function createDroneMappingUrl(arquivoPath, expiresIn = 7200) {
  try {
    const { data, error } = await supabase.storage
      .from(DRONE_MAPPING_BUCKET)
      .createSignedUrl(arquivoPath, expiresIn);
    if (error) throw error;
    return { success: true, url: data.signedUrl };
  } catch (err) {
    console.error('Erro ao gerar acesso ao mapeamento de drone:', err);
    return { success: false, error: err.message };
  }
}
